import AdminRezeptLimitListTemplate from '@/components/templates/AdminRezeptLimitListTemplate';
import { useAdminRezeptLimitList } from '@/hooks/admin/useAdminRezeptLimitList';
import { useCreateRezeptLimitMutation, useUpdateRezeptLimitMutation, useBulkCreateRezeptLimitsMutation } from '@/hooks/admin/useAdminRezeptLimitMutations';
import { useRedirectWhenPageEmpty } from '@/hooks/useRedirectWhenPageEmpty';
import { getRezeptLimitDetail } from '@/services/admin/rezeptLimitService';
import { getApiErrorMessage } from '@/utils/apiError';
import useAppToast from '@/hooks/useAppToast';
import type { RezeptLimit } from '@/types/admin-rezept-limit';
import type { SorterResult } from 'antd/es/table/interface';
import { useSearchParams, useParams } from 'react-router-dom';
import { useCallback, useState } from 'react';

const SiteAdminRezeptLimitsPage = () => {
  const { site } = useParams<{ site: string }>();
  const currentSite = site ?? 'medix';
  const [searchParams, setSearchParams] = useSearchParams();
  const page = searchParams.get('page') || '1';
  const sortByParam = searchParams.get('sort_by') || undefined;
  const orderParam = (searchParams.get('order') as 'ascend' | 'descend') || undefined;
  const searchYearParam = searchParams.get('search_rezept_yearmonth') || '';

  const [searchYear, setSearchYear] = useState(searchYearParam);

  const { data, isLoading, isError: listError } = useAdminRezeptLimitList({
    page: parseInt(page, 10) || 1,
    per_page: 20,
    search_rezept_yearmonth: searchYearParam || undefined,
    sort_by: sortByParam,
    order: orderParam,
  });

  const toast = useAppToast();
  const { createMutation, isPending: isCreating } = useCreateRezeptLimitMutation(currentSite);
  const { updateMutation, isPending: isEditing } = useUpdateRezeptLimitMutation(currentSite);
  const { bulkCreateMutation, isPending: isBulkCreating } = useBulkCreateRezeptLimitsMutation(currentSite);

  const handleCreate = useCallback(
    async (formData: import('@/types/admin-rezept-limit').RezeptLimitFormData) => {
      await createMutation(formData);
    },
    [createMutation]
  );

  const handleEdit = useCallback(
    async (id: number, formData: import('@/types/admin-rezept-limit').RezeptLimitFormData) => {
      await updateMutation({ id, data: formData });
    },
    [updateMutation]
  );

  const handleFetchDetail = useCallback(
    async (id: number): Promise<RezeptLimit | null> => {
      try {
        return await getRezeptLimitDetail(currentSite, id);
      } catch (error) {
        toast.error(getApiErrorMessage(error, 'エラーが発生しました。しばらくしてから再度お試しください。'));
        return null;
      }
    },
    [currentSite, toast]
  );

  const listData = data?.data ?? [];
  useRedirectWhenPageEmpty({
    searchParams,
    setSearchParams,
    isLoading,
    isError: listError,
    dataLength: listData.length,
  });

  const breadcrumbItems = [{ title: '提出期限' }];

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', newPage.toString());
    setSearchParams(params);
  };

  const handleTableChange = (
    sorter: SorterResult<RezeptLimit> | SorterResult<RezeptLimit>[]
  ) => {
    const params = new URLSearchParams(searchParams);
    const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter;

    if (singleSorter.order) {
      params.set('sort_by', (singleSorter.field || singleSorter.columnKey) as string);
      params.set('order', singleSorter.order);
    } else {
      params.delete('sort_by');
      params.delete('order');
    }
    params.set('page', '1');
    setSearchParams(params);
  };

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (searchYear) {
      params.set('search_rezept_yearmonth', searchYear);
    }
    params.set('page', '1');
    setSearchParams(params);
  };

  return (
    <AdminRezeptLimitListTemplate
      data={listData}
      total={data?.meta.total ?? 0}
      current={data?.meta.current_page ?? 1}
      pageSize={data?.meta.per_page ?? 20}
      sortInfo={{ columnKey: sortByParam, order: orderParam }}
      isLoading={isLoading}
      onChange={handlePageChange}
      onTableChange={handleTableChange}
      breadcrumbItems={breadcrumbItems}
      searchYear={searchYear}
      onSearchYear={setSearchYear}
      onSearch={handleSearch}
      onCreate={handleCreate}
      isCreating={isCreating}
      onEdit={handleEdit}
      isEditing={isEditing}
      onFetchDetail={handleFetchDetail}
      onBulkCreate={bulkCreateMutation}
      isBulkCreating={isBulkCreating}
    />
  );
};

export default SiteAdminRezeptLimitsPage;
